import { useEffect, useState } from 'react';
import { Download, FileText, X } from 'lucide-react';

interface AllegatoPreviewModalProps {
  presignedUrl: string;
  filename: string;
  contentType?: string | null;
  onClose: () => void;
}

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp'];

type PreviewKind = 'pdf' | 'image' | 'unsupported';

function getExtension(filename: string): string {
  const idx = filename.lastIndexOf('.');
  if (idx === -1 || idx === filename.length - 1) return '';
  return filename.slice(idx + 1).toLowerCase();
}

export function getPreviewKind(filename: string, contentType?: string | null): PreviewKind {
  const ext = getExtension(filename);
  const mime = (contentType || '').toLowerCase();
  if (ext === 'pdf' || mime === 'application/pdf') return 'pdf';
  if (IMAGE_EXTENSIONS.includes(ext) || mime.startsWith('image/')) return 'image';
  return 'unsupported';
}

export default function AllegatoPreviewModal({
  presignedUrl,
  filename,
  contentType,
  onClose,
}: AllegatoPreviewModalProps) {
  const kind = getPreviewKind(filename, contentType);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const downloadButton = (
    <a
      href={presignedUrl}
      target="_blank"
      rel="noopener noreferrer"
      download={filename}
      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
    >
      <Download className="h-4 w-4" />
      Scarica
    </a>
  );

  const fallback = (message: string) => (
    <div className="flex flex-col items-center justify-center h-full text-center p-8">
      <FileText className="h-16 w-16 text-gray-300 mb-4" />
      <p className="text-sm text-gray-600 mb-4">{message}</p>
      {downloadButton}
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Anteprima ${filename}`}
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900 truncate" title={filename}>
            {filename}
          </h3>
          <div className="flex items-center gap-2 flex-shrink-0">
            {kind !== 'unsupported' && downloadButton}
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600"
              title="Chiudi"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 bg-gray-100 rounded-b-lg overflow-auto">
          {kind === 'pdf' && (
            <iframe src={presignedUrl} title={filename} className="w-full h-full border-0" />
          )}
          {kind === 'image' &&
            (imageError ? (
              fallback("Impossibile caricare l'anteprima dell'immagine.")
            ) : (
              <div className="flex items-center justify-center h-full p-4">
                <img
                  src={presignedUrl}
                  alt={filename}
                  onError={() => setImageError(true)}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ))}
          {kind === 'unsupported' && fallback('Anteprima non disponibile per questo formato.')}
        </div>
      </div>
    </div>
  );
}
